import axios, { AxiosError } from "axios";
import Post from "@/models/post";
import User from "@/models/user";
import { Location } from "@/models/locationModel";
import { error } from '@/utils/logger';
import type { ShareContentType } from '@/lib/analyticsEvents';
import { appendShareAttributionToSearchParams, type ShareAttributionResourceType } from '@/lib/utm';
import type { PhotoIdentifier } from '@/utils/photoUtils';
import type {
  RecommendedLocation,
  RecommendationLocationsPaginatedResponse,
} from '@/services/recommendationService';
import type { ListMyRecommendationsResponse } from '@/api/enrichedRecommendation';
import type { ProfileStats } from '@/api/auth';
import { buildPostCanonicalPath, type CanonicalPostInput } from '@/utils/postUrl';
const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;

// Share link generation endpoint
export const GENERATE_SHARE = `${API_URL}/api/v2/share/generate`;

// Public access endpoints
export const PUBLIC_POST_BY_ID = `${API_URL}/api/v1/public/post`;
export const PUBLIC_PROFILE_BY_ID = `${API_URL}/api/v1/public/profile`;
export const PUBLIC_PROFILE_COMPLETE = `${API_URL}/api/v1/public/profile`;
export const PUBLIC_LOCATION_BY_ID = `${API_URL}/api/v1/public/location`;
export const PUBLIC_LOCATION_SHARE_BUNDLE = `${API_URL}/api/v1/public/share/location-with-sharer`;

// Share token management endpoint
export const REVOKE_SHARE_TOKEN = `${API_URL}/api/v2/share/token`;

// Public recommendation endpoint (token-gated, no auth required)
export const PUBLIC_RECOMMENDATION_BY_ID = `${API_URL}/api/v1/public/recommendation`;

/**
 * Single-token logged-out bootstrap for recommendation share landings.
 * Returns full recommender profile surface + merged pins + shared place + recommendation doc.
 * Mirrors the shape of location-with-sharer.
 */
export const PUBLIC_RECOMMENDATION_SHARE_BUNDLE = `${API_URL}/api/v1/public/share/recommendation-with-sharer`;

export type ShareResourceType = 'post' | 'profile' | 'location' | 'recommendation';

export interface ShareLinkRequest {
  resource_type: ShareResourceType;
  resource_id: string;
  /** Optional creator handle for ref= on attributed long URLs (share_url still preferred for copy). */
  creator_ref?: string;
}

/**
 * Backend response for share link generation
 * Backend returns resource info and token, frontend constructs the URL
 */
export interface ShareLinkBackendResponse {
  resource_type: ShareResourceType;
  resource_id: string;
  share_token: string;
  expires_at: string;
  created_by?: string; // User ID of the creator who generated the link
  short_code?: string; // Base62 short code for the short link (e.g. "Xk9mP2")
  short_url?: string; // Full short URL built by the backend (e.g. "https://app.globethrivers.com/s/Xk9mP2")
}

/**
 * Frontend response after constructing the share URL
 */
export interface ShareLinkResponse {
  share_url: string; // Preferred share URL: short when available, long URL as fallback — always use for external copy/share
  canonical_url?: string; // In-app URL without share attribution; do not use for creator marketing links
  share_token: string;
  expires_at: string;
  resource_type: ShareResourceType;
  resource_id: string;
  created_by?: string; // User ID of the creator who generated the link
  short_code?: string; // Present when backend returned a short code
  short_url?: string; // Present when backend returned a short URL
}

/**
 * Public location data returned by the token-gated public location endpoint.
 * Aligns with post/location payloads: use photoFormat=identifiers for photos on image_urls.
 */
export interface PublicGooglePlacesDataSlice {
  photos?: PhotoIdentifier[];
  formattedAddress?: string;
  formatted_address?: string;
  location?: { latitude?: number; longitude?: number };
  types?: string[];
  displayName?: { text?: string };
  /** Additional Places fields may be present */
  [key: string]: unknown;
}

/**
 * Public location data returned by the token-gated public location endpoint.
 * Contains canonical place fields only — no UGC.
 */
export interface PublicLocationData {
  _id: string;
  name?: string;
  description?: string;
  address?: string;
  a3_intl_address?: string;
  phone?: string;
  website?: string;
  /** Hero image URL when backend sends a resolved URL */
  main_photo?: string;
  /**
   * S3/HTTPS URLs and/or photo identifier objects (same as posts when photoFormat=identifiers).
   */
  image_urls?: unknown[];
  /** Top-level Google photo identifiers (backend may send here or under google_places_data) */
  photos?: PhotoIdentifier[];
  google_places_data?: PublicGooglePlacesDataSlice;
  gt_category?: 'eat' | 'stay' | 'do';
  category_name?: string;
  google_types?: string[];
  google_place_id?: string;
  nuitee_hotel_id?: string;
  geoposition?: {
    type?: string;
    coordinates?: number[]; // GeoJSON [lng, lat]
  };
  rec_count_breakdown?: {
    post_locations?: number;
    explicit_recommendations?: number;
  };
  traveller_types?: string[];
}

/** Pick string field supporting both snake_case (API docs) and camelCase (Node/Express JSON). */
function pickShareField(
  raw: Record<string, unknown>,
  snake: string,
  camel: string
): string | undefined {
  const a = raw[snake];
  const b = raw[camel];
  const v = a !== undefined && a !== null ? a : b;
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/**
 * Build the canonical in-app URL for a shared resource. For `profile` and
 * `location` shares, pass `ownerProfileType` so creators and advisors emit
 * their storefront URL (`/storefront/<id>`) rather than the plain profile
 * form — matches the term of art they share off-app. Travelers (and any
 * caller that omits the field) stay on `/profile/<id>`.
 */
export function buildCanonicalShareUrl(
  resourceType: ShareResourceType,
  resourceId: string,
  options?: {
    contentType?: ShareContentType;
    origin?: string;
    ownerProfileType?: string | null;
    /** Optional post record used to build the readable `/<segment>/<slug>-<short_id>` URL for Itinerary / List shares. */
    post?: CanonicalPostInput | null;
  }
): string {
  const origin =
    options?.origin ??
    (typeof window !== 'undefined' ? window.location.origin : 'https://globethrivers.com');

  const profilePrefix =
    options?.ownerProfileType === 'creator' || options?.ownerProfileType === 'advisor'
      ? '/storefront'
      : '/profile';

  switch (resourceType) {
    case 'profile':
      return `${origin}${profilePrefix}/${encodeURIComponent(resourceId)}`;
    case 'recommendation':
      return `${origin}/recommendation/${encodeURIComponent(resourceId)}`;
    case 'post': {
      const contentType = options?.contentType;
      // Prefer the readable `/itinerary/<slug>-<short_id>` or
      // `/list/<slug>-<short_id>` path when the caller passed the full post
      // record. Falls back to the legacy /<segment>/<_id> form when the post
      // has no short_id yet (pre-backfill).
      if ((contentType === 'list' || contentType === 'itinerary') && options?.post) {
        const canonicalPath = buildPostCanonicalPath(options.post);
        if (canonicalPath) {
          return `${origin}${canonicalPath}`;
        }
      }
      if (contentType === 'list') {
        return `${origin}/list/${encodeURIComponent(resourceId)}`;
      }
      if (contentType === 'place') {
        return `${origin}/place/${encodeURIComponent(resourceId)}`;
      }
      if (contentType === 'itinerary') {
        return `${origin}/itinerary/${encodeURIComponent(resourceId)}`;
      }
      return `${origin}/post/${encodeURIComponent(resourceId)}`;
    }
    case 'location':
      return `${origin}${profilePrefix}/${encodeURIComponent(resourceId)}`;
    default:
      return `${origin}/post/${encodeURIComponent(resourceId)}`;
  }
}

/**
 * Normalize POST /share/generate JSON — backend may return snake_case or camelCase.
 */
function normalizeShareGeneratePayload(raw: unknown): ShareLinkBackendResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;

  const share_token = pickShareField(d, 'share_token', 'shareToken');
  const resource_id = pickShareField(d, 'resource_id', 'resourceId');
  const resource_typeRaw = pickShareField(d, 'resource_type', 'resourceType');
  const expires_at = pickShareField(d, 'expires_at', 'expiresAt');

  if (!share_token || !resource_id || !resource_typeRaw) {
    return null;
  }

  const resource_type = resource_typeRaw as ShareResourceType;
  const created_by = pickShareField(d, 'created_by', 'createdBy');
  const short_code = pickShareField(d, 'short_code', 'shortCode');
  const short_url = pickShareField(d, 'short_url', 'shortUrl');

  return {
    resource_type,
    resource_id,
    share_token,
    expires_at: expires_at ?? '',
    ...(created_by && { created_by }),
    ...(short_code && { short_code }),
    ...(short_url && { short_url }),
  };
}

/**
 * Generate a share link for a post or profile
 * Backend returns resource info and token, frontend constructs the URL
 * This approach works automatically across all environments (localhost, staging, production)
 */
export async function generateShareLink(
  request: ShareLinkRequest,
  options?: {
    contentType?: ShareContentType;
    ownerProfileType?: string | null;
    /** Pass the Trip/List post record so canonical_url resolves to the readable `/<segment>/<slug>-<short_id>` URL. */
    post?: CanonicalPostInput | null;
  }
): Promise<ShareLinkResponse> {
  try {
    const response = await axios.post(GENERATE_SHARE, request);
    const data = normalizeShareGeneratePayload(response.data);

    if (!data) {
      error('[generateShareLink] Unrecognized response shape', { keys: response.data && typeof response.data === 'object' ? Object.keys(response.data as object) : [] });
      throw new Error('Invalid response from backend: missing required fields');
    }

    const currentOrigin = typeof window !== 'undefined'
      ? window.location.origin
      : 'https://globethrivers.com';

    // Prefer short URL from backend when present; fall back to long URL construction.
    // Long URL includes UTM attribution params when created_by is set.
    let shareUrl: string;
    if (data.short_url) {
      shareUrl = data.short_url;
    } else if (data.short_code) {
      shareUrl = `${currentOrigin}/s/${data.short_code}`;
    } else {
      const redirectParams = new URLSearchParams();
      redirectParams.set('path', `${data.resource_type}/${data.resource_id}`);
      redirectParams.set('token', data.share_token);
      if (data.created_by) {
        appendShareAttributionToSearchParams(redirectParams, {
          createdBy: data.created_by,
          resourceType: data.resource_type as ShareAttributionResourceType,
          resourceId: data.resource_id,
          ...(request.creator_ref?.trim() && { ref: request.creator_ref.trim() }),
        });
      }
      shareUrl = `${currentOrigin}/redirect/?${redirectParams.toString()}`;
    }

    return {
      share_url: shareUrl,
      canonical_url: buildCanonicalShareUrl(data.resource_type, data.resource_id, {
        contentType: options?.contentType,
        origin: currentOrigin,
        ownerProfileType: options?.ownerProfileType,
        post: options?.post,
      }),
      share_token: data.share_token,
      expires_at: data.expires_at,
      resource_type: data.resource_type,
      resource_id: data.resource_id,
      created_by: data.created_by,
      short_code: data.short_code,
      short_url: data.short_url,
    };
  } catch (err) {
    error('[generateShareLink] Error generating share link:', err);
    if (axios.isAxiosError(err)) {
      const axiosError = err as AxiosError<{ message?: string }>;
      throw new Error(axiosError.response?.data?.message || 'Failed to generate share link');
    }
    throw err instanceof Error ? err : new Error('Failed to generate share link');
  }
}

/**
 * Get a public post by ID using a share token. Returns both the constructed `Post` and
 * the raw API record so route loaders can cache the record alongside the model.
 */
export async function getPublicPostByIdWithRaw(
  postId: string,
  token?: string | null,
  options?: { signal?: AbortSignal }
): Promise<{ post: Post; raw: Record<string, unknown> }> {
  try {
    const params: Record<string, string> = { photoFormat: 'identifiers' };
    if (token) params.token = token;
    const response = await axios.get(`${PUBLIC_POST_BY_ID}/${postId}`, {
      params,
      signal: options?.signal,
    });
    const raw = (response.data ?? {}) as Record<string, unknown>;
    return { post: new Post(raw), raw };
  } catch (err) {
    error('[getPublicPostById] Error fetching public post:', err);
    if (axios.isAxiosError(err)) {
      // Re-throw to preserve status code for error handling
      throw err;
    }
    throw err instanceof Error ? err : new Error('Failed to fetch public post');
  }
}

/**
 * Get a public post by ID using a share token.
 */
export async function getPublicPostById(
  postId: string,
  token?: string | null,
  options?: { signal?: AbortSignal }
): Promise<Post> {
  const { post } = await getPublicPostByIdWithRaw(postId, token, options);
  return post;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Get a public location by ID using a share token.
 * Calls the token-gated public location endpoint (401/403/404 on invalid/expired/missing token).
 * Retries a few times on 429 to reduce failures from rate limits.
 */
export async function getPublicLocationById(
  locationId: string,
  token: string
): Promise<PublicLocationData> {
  const maxAttempts = 3;
  let lastErr: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await axios.get<PublicLocationData>(
        `${PUBLIC_LOCATION_BY_ID}/${locationId}`,
        {
          params: { token, photoFormat: 'identifiers' },
        }
      );
      return response.data;
    } catch (err) {
      lastErr = err;
      if (
        axios.isAxiosError(err) &&
        err.response?.status === 429 &&
        attempt < maxAttempts
      ) {
        const header = err.response.headers?.['retry-after'];
        const retryAfterSec =
          typeof header === 'string' ? parseInt(header, 10) : Number(header);
        const waitMs =
          Number.isFinite(retryAfterSec) && retryAfterSec > 0
            ? retryAfterSec * 1000
            : 400 * attempt;
        await sleep(waitMs);
        continue;
      }
      error('[getPublicLocationById] Error fetching public location:', err);
      if (axios.isAxiosError(err)) {
        throw err;
      }
      throw err instanceof Error ? err : new Error('Failed to fetch public location');
    }
  }

  throw lastErr instanceof Error ? lastErr : new Error('Failed to fetch public location');
}

function formattedAddressFromGooglePlaces(
  gpd: PublicGooglePlacesDataSlice | undefined
): string | undefined {
  if (!gpd || typeof gpd !== 'object') return undefined;
  const fa = gpd.formattedAddress ?? gpd.formatted_address;
  return typeof fa === 'string' && fa.trim() ? fa.trim() : undefined;
}

/**
 * Map public location photo fields into the same shape as authenticated location/post payloads
 * so extractPhotoUrl / extractPhotosFromLocation use the standard fallback chain.
 */
function pickPublicPhotoFieldsForLocation(data: PublicLocationData): Partial<Location> & {
  image_urls?: unknown[];
} {
  const out: Partial<Location> & { image_urls?: unknown[] } = {};

  const main =
    typeof data.main_photo === 'string' && data.main_photo.trim().length > 0
      ? data.main_photo.trim()
      : undefined;
  if (main) {
    out.main_photo = main;
  }

  let imageUrls: unknown[] | undefined;
  if (Array.isArray(data.image_urls) && data.image_urls.length > 0) {
    imageUrls = data.image_urls.filter((e) => e != null && e !== '');
  }
  if ((!imageUrls || imageUrls.length === 0) && main) {
    imageUrls = [main];
  }
  if (imageUrls && imageUrls.length > 0) {
    (out as Record<string, unknown>).image_urls = imageUrls;
  }

  if (data.photos && data.photos.length > 0) {
    out.photos = data.photos as Location['photos'];
  }

  if (data.google_places_data && typeof data.google_places_data === 'object') {
    out.google_places_data = data.google_places_data as Location['google_places_data'];
  }

  return out;
}

/** Build a Location model from token-gated public location API (for modals / map markers). */
export function publicLocationDataToLocation(data: PublicLocationData): Location {
  const coords = data.geoposition?.coordinates;
  const geo_position =
    coords && coords.length >= 2
      ? { lat: Number(coords[1]), long: Number(coords[0]) }
      : undefined;

  const gpdAddr = formattedAddressFromGooglePlaces(data.google_places_data);
  const a3 =
    (typeof data.a3_intl_address === 'string' && data.a3_intl_address.trim()
      ? data.a3_intl_address.trim()
      : undefined) ??
    (typeof data.address === 'string' && data.address.trim() ? data.address.trim() : undefined) ??
    gpdAddr;

  const photoFields = pickPublicPhotoFieldsForLocation(data);

  return new Location({
    _id: data._id,
    name: data.name ?? '',
    description: data.description,
    address: data.address,
    a3_intl_address: a3,
    phone: data.phone,
    website: data.website,
    gt_category: data.gt_category,
    category_name: data.category_name,
    google_types: data.google_types,
    google_place_id: data.google_place_id,
    nuitee_hotel_id: data.nuitee_hotel_id,
    geoposition: data.geoposition as Location['geoposition'],
    geo_position,
    rec_count_breakdown: data.rec_count_breakdown,
    traveller_types: data.traveller_types,
    ...photoFields,
  } as Partial<Location>);
}

/**
 * Get a public profile by ID using a share token (basic fields only).
 * NOTE: This endpoint does NOT increment access_count. Use getPublicProfileComplete
 * for the shared profile page so that DB visits align with Mixpanel "Share Link Viewed".
 * This function is retained for cases where only basic profile fields are needed
 * and getPublicProfileComplete is not already being called on the same page.
 * @deprecated Prefer getPublicProfileComplete for shared profile page loads.
 */
export async function getPublicProfileById(
  profileId: string,
  token?: string
): Promise<User> {
  try {
    const params: Record<string, string> = {};
    if (token) params.token = token;
    const response = await axios.get(`${PUBLIC_PROFILE_BY_ID}/${profileId}`, {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return new User(response.data);
  } catch (err) {
    error('[getPublicProfileById] Error fetching public profile:', err);
    if (axios.isAxiosError(err)) {
      // Re-throw to preserve status code for error handling
      throw err;
    }
    throw err instanceof Error ? err : new Error('Failed to fetch public profile');
  }
}

/**
 * Public Profile Page Data Response Structure
 */
export interface PublicProfileCompleteResponse {
  profile: {
    _id: string;
    name: string;
    bio?: string;
    large_avatar_url?: string;
    small_avatar_url?: string;
    avatar_url?: string;
    profile_image_url?: string;
    hero_image_url?: string;
    hero_image_secondary_url?: string;
    city?: {
      _id: string;
      short_name: string;
      long_name: string;
      identifying_name: string;
      city_identifying_name: string;
      coordinates?: {
        latitude: number;
        longitude: number;
      };
      google_places_data?: unknown;
    };
    city_identifying_name?: string;
    instagram_username?: string;
    tiktok_username?: string;
    youtube_username?: string;
    substack_username?: string;
    website_url?: string;
    followers_count: number;
    following_count: number;
    post_count: number;
    recommendation_count: number;
    saves_count: number;
    remixes_count: number;
    profile_type?: string;
    traveller_types?: string[];
    travel_with_types?: string[];
    is_following?: boolean;
    is_influencer?: boolean;
    [key: string]: unknown;
  };
  posts: {
    pinned: unknown[];
    itineraries: unknown[];
    lists: unknown[];
    posts: unknown[];
    metadata: {
      totalItineraries: number;
      totalLists: number;
      totalPosts: number;
      totalPinned: number;
      hasMoreItineraries: boolean;
      hasMoreLists: boolean;
      hasMorePosts: boolean;
      isSample: boolean;
    };
  };
  countryCount: number;
  metadata?: {
    tokenValid: boolean;
    isPublicView: boolean;
    renderedAt: string;
  };
}

/**
 * Option C: GET /api/v1/public/share/location-with-sharer
 * Same profile + posts + countryCount shape as /public/profile/:id/complete, plus shared location and map pins.
 *
 * recommendation_locations: plain Location[] (public rec pins for the sharer + shared place merged on the server).
 * Shared location is prepended when not already in the sharer’s public pins; no duplicate _id.
 * When profile_available is false: profile/posts may be null; recommendation_locations is still populated
 * (typically [location] only) so the map can show the shared pin — do not treat null/empty as “skip map”.
 * photoFormat=identifiers applies to recommendation_locations and the top-level location object.
 *
 * map_focus (optional): server-derived map camera for shared-place context (city_db → poi → pins_bounds).
 * Omitted when no center can be derived — not null. Only on this endpoint, not on profile /complete or recommendation_locations.
 */
export type LocationShareMapFocusSource = "city_db" | "poi" | "pins_bounds";

export interface LocationShareMapFocus {
  center: { lat: number; lng: number };
  /** Server hint (e.g. 11 city/pins bounds, 14 poi); clients may clamp for layout */
  zoom_hint?: number;
  source?: LocationShareMapFocusSource;
}

export interface LocationShareBundleResponse {
  location: unknown;
  profile: PublicProfileCompleteResponse["profile"] | null;
  posts: PublicProfileCompleteResponse["posts"] | null;
  countryCount: number | null;
  recommendation_locations: unknown[] | null;
  profile_available: boolean;
  map_focus?: LocationShareMapFocus;
  metadata?: PublicProfileCompleteResponse["metadata"] & {
    share_type?: string;
  };
}

/**
 * Single-token logged-out bootstrap for recommendation share landings.
 * Same top-level shape as LocationShareBundleResponse plus a `recommendation` doc.
 * One recommendation share token returns: full recommender profile surface (profile, posts,
 * countryCount), merged recommendation_locations with shared place guaranteed, the linked
 * location, and the recommendation document itself.
 * profile_available: false degrades gracefully (recommendation + location still returned).
 */
export interface RecommendationShareBundleResponse {
  recommendation: unknown | null;
  location: unknown;
  profile: PublicProfileCompleteResponse["profile"] | null;
  posts: PublicProfileCompleteResponse["posts"] | null;
  countryCount: number | null;
  recommendation_locations: unknown[] | null;
  profile_available: boolean;
  map_focus?: LocationShareMapFocus;
  metadata?: PublicProfileCompleteResponse["metadata"] & {
    share_type?: string;
  };
}

/**
 * Fetch recommender profile surface + recommendation + merged map pins with one share token.
 * Use this for logged-out recommendation share landings; avoids any need for a separate profile token.
 * photoFormat=identifiers strips direct Google photo URLs on location pins.
 */
export async function getRecommendationShareBundle(
  token: string,
  recommendationId: string
): Promise<RecommendationShareBundleResponse> {
  const params = new URLSearchParams({
    token,
    recommendation_id: recommendationId,
    photoFormat: "identifiers",
  });
  const response = await axios.get<RecommendationShareBundleResponse>(
    `${PUBLIC_RECOMMENDATION_SHARE_BUNDLE}?${params.toString()}`
  );
  return response.data;
}

/**
 * Fetch sharer + location + public profile page data using the location share token (no profile token required).
 * photoFormat=identifiers strips direct Google photo URLs from rec-location pins, matching the profile-share path.
 */
export async function getLocationShareBundle(
  token: string,
  locationId: string,
  profileId: string
): Promise<LocationShareBundleResponse> {
  const params = new URLSearchParams({
    token,
    photoFormat: "identifiers",
    location_id: locationId,
    profile_id: profileId,
  });
  const response = await axios.get<LocationShareBundleResponse>(
    `${PUBLIC_LOCATION_SHARE_BUNDLE}?${params.toString()}`
  );
  return response.data;
}

/**
 * Get complete public profile page data using public profile endpoint (for logged-out users with token)
 * Returns all data needed to render profile page: profile, posts, stats
 */
export async function getPublicProfileComplete(
  profileId: string,
  token?: string,
  options?: { signal?: AbortSignal }
): Promise<PublicProfileCompleteResponse> {
  try {
    const params: Record<string, string> = {};
    if (token) params.token = token;
    const url = `${PUBLIC_PROFILE_COMPLETE}/${profileId}/complete`;
    const response = await axios.get<PublicProfileCompleteResponse>(
      url,
      { params, signal: options?.signal }
    );
    return response.data;
  } catch (err) {
    error('[getPublicProfileComplete] Error fetching complete profile data:', err);
    if (axios.isAxiosError(err)) {
      // Re-throw to preserve status code for error handling
      throw err;
    }
    throw err instanceof Error ? err : new Error('Failed to fetch complete profile data');
  }
}

/** Logged-out Countries tab: the profile's countries with post counts only (no post ids or details). */
export interface PublicUserCountriesResponse {
  count: number;
  countries: string[];
  posts_by_country: Record<string, { post_count: number }>;
}

/**
 * Country list for logged-out profile views.
 * GET /api/v1/public/profile/:userId/countries?token=
 * Profile share token, or none in OPEN browse mode. Throws on failure (401/403/404/429).
 */
export async function getPublicUserCountries(
  userId: string,
  token?: string | null
): Promise<PublicUserCountriesResponse> {
  const params: Record<string, string> = {};
  if (token) params.token = token;
  const response = await axios.get<PublicUserCountriesResponse>(
    `${API_URL}/api/v1/public/profile/${encodeURIComponent(userId)}/countries`,
    { params }
  );
  return response.data;
}

/**
 * Profile totals for logged-out profile views (same numbers a signed-in viewer gets).
 * GET /api/v1/public/profile/:userId/stats?token=
 * Profile share token, or none in OPEN browse mode. Throws on failure.
 */
export async function getPublicUserProfileStats(
  userId: string,
  token?: string | null
): Promise<ProfileStats> {
  const params: Record<string, string> = {};
  if (token) params.token = token;
  const response = await axios.get<ProfileStats>(
    `${API_URL}/api/v1/public/profile/${encodeURIComponent(userId)}/stats`,
    { params }
  );
  return response.data;
}

/**
 * Experiences tab for logged-out profile views: places from published Lists/Trips plus the
 * user's own recommendations, as plain Location documents (public fields + the Google types,
 * address parts and coordinates the tab classifies by).
 * GET /api/v1/public/profile/:userId/experiences?token=
 * Profile share token, or none in OPEN browse mode. Throws on failure.
 */
export async function getPublicUserExperiences(
  userId: string,
  token?: string | null
): Promise<Location[]> {
  const params: Record<string, string> = {};
  if (token) params.token = token;
  const response = await axios.get<Location[]>(
    `${API_URL}/api/v1/public/profile/${encodeURIComponent(userId)}/experiences`,
    { params }
  );
  return response.data;
}

/**
 * One page of the user's own recommendations for logged-out profile views, in the same
 * { items, pagination } shape as listUserRecommendationsForViewer(..., { excludeAuto: true }).
 * GET /api/v1/public/profile/:userId/recommendations?token=&page=&limit=
 * Profile share token, or none in OPEN browse mode. Throws on failure.
 */
export async function listPublicUserRecommendations(
  userId: string,
  token: string | null | undefined,
  { page, limit }: { page: number; limit: number }
): Promise<ListMyRecommendationsResponse> {
  const params: Record<string, string | number> = { page, limit };
  if (token) params.token = token;
  const response = await axios.get<ListMyRecommendationsResponse>(
    `${API_URL}/api/v1/public/profile/${encodeURIComponent(userId)}/recommendations`,
    { params }
  );
  return response.data;
}

/**
 * Recommendation map pins for logged-out profile views (profile share token only — not a location token).
 * GET /api/v1/public/profile/:userId/recommendation_locations?token=&page=&limit=&photoFormat=
 * Omit page & limit for a plain array. Same paginated shape as the authed user route when paginated.
 * Public share routes are rate-limited (429); callers may retry or cache per token.
 */
export async function getPublicProfileRecommendationLocationsPaginated(
  userId: string,
  token: string,
  page: number,
  limit: number,
  options?: { signal?: AbortSignal }
): Promise<RecommendationLocationsPaginatedResponse> {
  const endpoint = `${API_URL}/api/v1/public/profile/${userId}/recommendation_locations`;
  const params = {
    token,
    photoFormat: 'identifiers' as const,
    page,
    limit: Math.min(100, Math.max(1, limit)),
  };
  try {
    const response = await axios.get<
      RecommendationLocationsPaginatedResponse | RecommendedLocation[]
    >(endpoint, { params, signal: options?.signal });
    const data = response.data;
    if (Array.isArray(data)) {
      return {
        locations: data,
        pagination: {
          page: 1,
          limit: data.length,
          total: data.length,
          total_pages: 1,
          has_next: false,
          has_previous: false,
        },
      };
    }
    return data;
  } catch (err) {
    error('[getPublicProfileRecommendationLocationsPaginated] Error fetching public recommendation locations:', err);
    if (axios.isAxiosError(err)) {
      throw err;
    }
    throw err instanceof Error ? err : new Error('Failed to fetch public recommendation locations');
  }
}

/**
 * Revoke a share token
 */
export async function revokeShareToken(token: string): Promise<void> {
  try {
    await axios.delete(`${REVOKE_SHARE_TOKEN}/${token}`);
  } catch (err) {
    error('[revokeShareToken] Error revoking share token:', err);
    if (axios.isAxiosError(err)) {
      const axiosError = err as AxiosError<{ message?: string }>;
      throw new Error(axiosError.response?.data?.message || 'Failed to revoke share token');
    }
    throw err instanceof Error ? err : new Error('Failed to revoke share token');
  }
}

/**
 * Revoke a share token (admin endpoint - can revoke any token)
 * Requires admin permissions
 */
export async function adminRevokeShareToken(token: string): Promise<void> {
  try {
    await axios.delete(`${ADMIN_DELETE_TOKEN}/${token}`);
  } catch (err) {
    error('[adminRevokeShareToken] Error revoking share token:', err);
    if (axios.isAxiosError(err)) {
      const axiosError = err as AxiosError<{ message?: string }>;
      throw new Error(axiosError.response?.data?.message || 'Failed to revoke share token');
    }
    throw err instanceof Error ? err : new Error('Failed to revoke share token');
  }
}

// Share tokens list endpoint
export const GET_SHARE_TOKENS = `${API_URL}/api/v2/share/tokens`;

// Admin share tokens endpoints
export const ADMIN_GET_ALL_TOKENS = `${API_URL}/api/v2/admin/share/tokens/all`;
export const ADMIN_SHARE_TOKENS_BY_USER = `${API_URL}/api/v2/admin/share/tokens/user`;
export const ADMIN_DELETE_TOKEN = `${API_URL}/api/v2/admin/share/token`;
export const ADMIN_SHARE_TOKENS_OWNED_RESOURCES = `${API_URL}/api/v2/admin/share/tokens/owned-resources`;
export const ADMIN_SHARE_GENERATE = `${API_URL}/api/v2/admin/share/generate`;

export interface ShareToken {
  share_token: string;
  /** Base62 short code for /s/:code links; null when the doc has none (admin list). */
  short_code?: string | null;
  created_at: string;
  expires_at: string;
  access_count: number;
  last_accessed_at: string | null;
  created_by?: string; // User ID who generated the token (included in list endpoints)
  created_by_name?: string | null; // User name who generated the token (included in list endpoints)
  resource_type?: ShareResourceType; // Included in admin endpoints
  resource_id?: string; // Included in admin endpoints
  is_active?: boolean; // Included in admin endpoints
  resource_name?: string | null; // Resolved resource name (post title or profile name) — admin endpoint only
  deactivated_reason?: string | null; // e.g. "resource_hidden", "owner_hidden" — null for active tokens
  deactivated_at?: string | null; // ISO date string when token was deactivated — null for active tokens
}

export interface AdminAllTokensResponse {
  tokens: ShareToken[];
  total: number;
  limit: number;
  skip: number;
}

export interface BulkShareTokensResponse {
  post?: Record<string, ShareToken[]>;
  profile?: Record<string, ShareToken[]>;
  location?: Record<string, ShareToken[]>;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Normalizes bulk share-token JSON from the API (unwrap `data`, accept key aliases).
 */
function normalizeBulkShareTokensPayload(raw: unknown): BulkShareTokensResponse {
  if (!isPlainObject(raw)) return {};

  let root: Record<string, unknown> = raw;
  const inner = raw.data;
  const groupedKeys = ['post', 'Post', 'profile', 'Profile', 'location', 'Location', 'locations', 'Locations'];
  if (isPlainObject(inner) && groupedKeys.some((k) => isPlainObject(inner[k]))) {
    root = inner;
  }

  const pickGrouped = (keys: string[]): Record<string, ShareToken[]> | undefined => {
    for (const k of keys) {
      const v = root[k];
      if (!isPlainObject(v)) continue;
      const out: Record<string, ShareToken[]> = {};
      for (const [id, arr] of Object.entries(v)) {
        if (Array.isArray(arr) && arr.length > 0) {
          out[id] = arr as ShareToken[];
        }
      }
      if (Object.keys(out).length > 0) return out;
    }
    return undefined;
  };

  const post = pickGrouped(['post', 'Post']);
  const profile = pickGrouped(['profile', 'Profile']);
  const locA = pickGrouped(['location', 'Location']);
  const locB = pickGrouped(['locations', 'Locations']);
  const location =
    locA && locB ? { ...locA, ...locB } : locA ?? locB;

  return {
    ...(post ? { post } : {}),
    ...(profile ? { profile } : {}),
    ...(location && Object.keys(location).length > 0 ? { location } : {}),
  };
}

/** Resolved display name on a token row (snake_case or camelCase from API). */
export function shareTokenResourceName(token: ShareToken | undefined): string | null {
  if (!token) return null;
  const row = token as ShareToken & { resourceName?: string | null };
  return token.resource_name ?? row.resourceName ?? null;
}

/**
 * Bulk tokens for the profile "Your Shared Resources" modal.
 * Uses include_owned=true (posts/profile others shared of your content). If that payload has no
 * `location` bucket (older backend or grouping quirk), merges `location` from GET /tokens (created_by = you only),
 * which already included location tokens before the owned-resources $or fix.
 */
export async function getBulkShareTokensForProfileModal(): Promise<BulkShareTokensResponse> {
  const ownedRes = await axios.get(`${GET_SHARE_TOKENS}?include_owned=true`);
  let bulk = normalizeBulkShareTokensPayload(ownedRes.data);

  const hasLocations = bulk.location && Object.keys(bulk.location).length > 0;
  if (!hasLocations) {
    try {
      const mineRes = await axios.get(GET_SHARE_TOKENS);
      const mine = normalizeBulkShareTokensPayload(mineRes.data);
      if (mine.location && Object.keys(mine.location).length > 0) {
        bulk = { ...bulk, location: mine.location };
      }
    } catch {
      /* keep bulk */
    }
  }

  return bulk;
}

/**
 * Get all active share tokens for a resource
 * @param resourceType - "post" | "profile"
 * @param resourceId - ObjectId string
 * @returns Array of share tokens
 */
export async function getShareTokensForResource(
  resourceType: 'post' | 'profile',
  resourceId: string
): Promise<ShareToken[]> {
  try {
    const response = await axios.get(`${GET_SHARE_TOKENS}/${resourceType}/${resourceId}`);
    const tokens = Array.isArray(response.data) ? response.data : [];
    return tokens;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const axiosError = err as AxiosError<{ message?: string }>;
      const status = axiosError.response?.status;
      
      // 404 means resource has no tokens
      if (status === 404) {
        return [];
      }
      
      // Only log non-expected errors
      if (status !== 401 && status !== 403) {
        error('[getShareTokensForResource] Error fetching share tokens:', err);
      }
      
      // For 401, let it propagate (auth system will handle)
      if (status === 401) {
        throw err;
      }
      
      // For other errors, return empty array to avoid breaking the flow
      return [];
    }
    
    // For non-axios errors, return empty array
    return [];
  }
}

/**
 * Get all share tokens for the authenticated user (bulk endpoint)
 * Returns tokens grouped by resource type and resource ID
 * @param includeOwned - If true, returns all tokens for resources owned by the user (created by anyone)
 *                        If false, returns only tokens created by the user (default behavior)
 * @returns Object with post and profile tokens grouped by resource ID
 */
export async function getAllShareTokens(includeOwned: boolean = false): Promise<BulkShareTokensResponse> {
  try {
    const endpoint = includeOwned 
      ? `${GET_SHARE_TOKENS}?include_owned=true`
      : GET_SHARE_TOKENS;
    const response = await axios.get(endpoint);
    return normalizeBulkShareTokensPayload(response.data);
  } catch (err) {
    error('[getAllShareTokens] Error fetching all share tokens:', err);
    if (axios.isAxiosError(err)) {
      const axiosError = err as AxiosError<{ message?: string }>;
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch all share tokens');
    }
    throw err instanceof Error ? err : new Error('Failed to fetch all share tokens');
  }
}

/**
 * Get all share tokens (admin endpoint - requires admin permissions)
 * Returns all tokens across all users with pagination
 * @param limit - Number of tokens to return (default: 100, max: 500)
 * @param skip - Number of tokens to skip for pagination (default: 0)
 * @param isActive - Filter by active status; true = active only, false = inactive only, undefined = all
 * @returns Paginated response with tokens array and total count
 */
function normalizeAdminAllTokensResponse(raw: unknown): AdminAllTokensResponse {
  if (Array.isArray(raw)) {
    return {
      tokens: raw as ShareToken[],
      total: raw.length,
      limit: raw.length,
      skip: 0,
    };
  }
  if (raw && typeof raw === 'object') {
    const data = raw as AdminAllTokensResponse;
    if (Array.isArray(data.tokens)) {
      return data;
    }
  }
  return { tokens: [], total: 0, limit: 0, skip: 0 };
}

export async function adminGetAllShareTokens(
  limit: number = 100,
  skip: number = 0,
  isActive?: boolean
): Promise<AdminAllTokensResponse> {
  try {
    const params: Record<string, unknown> = { limit, skip };
    if (isActive !== undefined) {
      params.is_active = isActive;
    }
    const response = await axios.get(ADMIN_GET_ALL_TOKENS, { params });
    return normalizeAdminAllTokensResponse(response.data);
  } catch (err) {
    error('[adminGetAllShareTokens] Error fetching all share tokens:', err);
    if (axios.isAxiosError(err)) {
      const axiosError = err as AxiosError<{ message?: string }>;
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch all share tokens');
    }
    throw err instanceof Error ? err : new Error('Failed to fetch all share tokens');
  }
}

/**
 * Get all share tokens created by a specific user (admin endpoint).
 * Returns the full set for that creator (no 100-token page cap).
 * Optional is_active filter matches GET /tokens/all semantics.
 */
export async function adminGetShareTokensByUser(
  userId: string,
  options?: { isActive?: boolean }
): Promise<AdminAllTokensResponse> {
  try {
    const params: Record<string, unknown> = {};
    if (options?.isActive !== undefined) {
      params.is_active = options.isActive;
    }
    const response = await axios.get(`${ADMIN_SHARE_TOKENS_BY_USER}/${userId}`, { params });
    return normalizeAdminAllTokensResponse(response.data);
  } catch (err) {
    error('[adminGetShareTokensByUser] Error fetching user share tokens:', err);
    if (axios.isAxiosError(err)) {
      const axiosError = err as AxiosError<{ message?: string }>;
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch user share tokens');
    }
    throw err instanceof Error ? err : new Error('Failed to fetch user share tokens');
  }
}

/**
 * Get bulk share tokens for a specific user's owned resources (admin endpoint).
 * Returns the same grouped shape as GET /api/v2/share/tokens?include_owned=true for that user:
 * active tokens on resources **owned** by targetUserId, including links created by other users.
 * Requires admin permissions.
 */
export async function getAdminBulkShareTokensForOwnedResources(
  targetUserId: string
): Promise<BulkShareTokensResponse> {
  try {
    const response = await axios.get(`${ADMIN_SHARE_TOKENS_OWNED_RESOURCES}/${targetUserId}`);
    return normalizeBulkShareTokensPayload(response.data);
  } catch (err) {
    error('[getAdminBulkShareTokensForOwnedResources] Error fetching owned-resources tokens:', err);
    if (axios.isAxiosError(err)) {
      const axiosError = err as AxiosError<{ message?: string }>;
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch shared resources');
    }
    throw err instanceof Error ? err : new Error('Failed to fetch shared resources');
  }
}

/**
 * Generate a share link attributed to a specific user (admin endpoint).
 * Uses POST /api/v2/admin/share/generate; created_by on the resulting token will be
 * created_by_user_id (the resource owner), not the admin's own user ID.
 * Requires admin permissions.
 */
export async function adminGenerateShareLink(
  params: {
    resource_type: ShareResourceType;
    resource_id: string;
    created_by_user_id: string;
  },
  options?: {
    ownerProfileType?: string | null;
    contentType?: ShareContentType;
    /** Pass the Trip/List post record so canonical_url resolves to the readable `/<segment>/<slug>-<short_id>` URL. */
    post?: CanonicalPostInput | null;
  },
): Promise<ShareLinkResponse> {
  try {
    const response = await axios.post(ADMIN_SHARE_GENERATE, params);
    const data = normalizeShareGeneratePayload(response.data);

    if (!data) {
      error('[adminGenerateShareLink] Unrecognized response shape', {
        keys: response.data && typeof response.data === 'object' ? Object.keys(response.data as object) : [],
      });
      throw new Error('Invalid response from backend: missing required fields');
    }

    const currentOrigin =
      typeof window !== 'undefined' ? window.location.origin : 'https://globethrivers.com';

    let shareUrl: string;
    if (data.short_url) {
      shareUrl = data.short_url;
    } else if (data.short_code) {
      shareUrl = `${currentOrigin}/s/${data.short_code}`;
    } else {
      const redirectParams = new URLSearchParams();
      redirectParams.set('path', `${data.resource_type}/${data.resource_id}`);
      redirectParams.set('token', data.share_token);
      if (data.created_by) {
        appendShareAttributionToSearchParams(redirectParams, {
          createdBy: data.created_by,
          resourceType: data.resource_type as ShareAttributionResourceType,
          resourceId: data.resource_id,
        });
      }
      shareUrl = `${currentOrigin}/redirect/?${redirectParams.toString()}`;
    }

    return {
      share_url: shareUrl,
      canonical_url: buildCanonicalShareUrl(data.resource_type, data.resource_id, {
        origin: currentOrigin,
        ownerProfileType: options?.ownerProfileType,
        contentType: options?.contentType,
        post: options?.post,
      }),
      share_token: data.share_token,
      expires_at: data.expires_at,
      resource_type: data.resource_type,
      resource_id: data.resource_id,
      created_by: data.created_by,
      short_code: data.short_code,
      short_url: data.short_url,
    };
  } catch (err) {
    error('[adminGenerateShareLink] Error generating share link:', err);
    if (axios.isAxiosError(err)) {
      const axiosError = err as AxiosError<{ message?: string }>;
      throw new Error(axiosError.response?.data?.message || 'Failed to generate share link');
    }
    throw err instanceof Error ? err : new Error('Failed to generate share link');
  }
}

